import { Box, Button, Grid, Input, Typography } from '@mui/material';
import { useRef, useState  } from 'react';
import { UploadFileOutlined } from '@mui/icons-material';
import { SRT_RESPONSE_RGRL_TXT_TYPE, SRT_RESPONSE_VISITS_TXT_TYPE, useUploadResponseSRTMutation, useUploadVisitsResponseSRTMutation } from '../../../services/rgrlVinculationApi';
import { enqueueSnackbar } from 'notistack';
import { useDispatch } from 'react-redux';
import { setPresentationUploadErrorLocationData } from '../../../redux/slices/srtPresentationsSlice';
import BackdropComponent from '../../../components/Backdrop/BackdropComponent';
import { TEST_IDS } from '../consts/testIds';
import { handleErrors } from '../../../utils/common';
import { useAppSelector } from '../../../hooks/reduxHooks';
import { DrawerHeader } from '../../../components/Drawer/DrawerHeader';
import { TAB_INDEX } from '../Index';

const VALID_RGRL_SRT_TYPES: SRT_RESPONSE_RGRL_TXT_TYPE[] = ['RB', 'RC', 'RH', 'RI', 'RM', 'RO', 'RS'];
const VALID_VISITS_SRT_TYPES: SRT_RESPONSE_VISITS_TXT_TYPE[] = ['VI', 'RD'];
const IS_VALID_SRT_RGRL_TYPE_REGEX = /\.(RB|RC|RH|RI|RM|RO|RS)\d*\.txt$/;
const IS_VALID_SRT_VISIT_TYPE_REGEX = /\.(VI|RD)\d*\.txt$/;

const UploadResponseSRTDrawer = ({ handleCloseDrawer, translate, currentTab }: any) => {
    const [uploadRGRLResponseSRT, { isLoading: isUploadingRGRLResponse }] = useUploadResponseSRTMutation();
    const [uploadVisitsResponseSRT, { isLoading: isUploadingVisitsResponse }] = useUploadVisitsResponseSRTMutation();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const dispatch = useDispatch();
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [fileName, setFileName] = useState<string | null>(null);
    const presentationUploadErrors = useAppSelector(
        (state) => state.srtPresentations.presentationUploadErrorLocationData
    );

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];

        if (!file) return;

        if (!file.name.endsWith('.txt')) {
            setSelectedFile(null);
            setFileName(null);
            enqueueSnackbar(translate('drawers.uploadResponseSRT.enqueues.invalidFileType'), {
                variant: 'error'
            });
            return;
        }
        let match =  null;
        
        if(currentTab === TAB_INDEX.RGRL) {
            match = file.name.match(IS_VALID_SRT_RGRL_TYPE_REGEX);
        }

        if(currentTab === TAB_INDEX.VISITS) {
            match = file.name.match(IS_VALID_SRT_VISIT_TYPE_REGEX);
        }

        if (!match) {
            setSelectedFile(null);
            setFileName(null);
            enqueueSnackbar(translate('drawers.uploadResponseSRT.enqueues.invalidFileName'), {
                variant: 'error'
            });
            return;
        }

        const nameType = match[1] as SRT_RESPONSE_RGRL_TXT_TYPE & SRT_RESPONSE_VISITS_TXT_TYPE;

        if ((currentTab === TAB_INDEX.RGRL && !VALID_RGRL_SRT_TYPES.includes(nameType))) {
            setSelectedFile(null);
            setFileName(null);
            enqueueSnackbar(translate('drawers.uploadResponseSRT.enqueues.invalidFileType'), {
                variant: 'error'
            });
            return;
        }

        if ((currentTab === TAB_INDEX.VISITS && !VALID_VISITS_SRT_TYPES.includes(nameType))) {
            setSelectedFile(null);
            setFileName(null);
            enqueueSnackbar(translate('drawers.uploadResponseSRT.enqueues.invalidFileType'), {
                variant: 'error'
            });
            return;
        }


        setSelectedFile(file);
        setFileName(file.name);
    };

    const handleSubmit = async () => {
        if (!selectedFile) {
            return;
        }

        let match = null
                
        if (currentTab === TAB_INDEX.RGRL) {
            match = selectedFile.name.match(IS_VALID_SRT_RGRL_TYPE_REGEX);
        }

        if (currentTab === TAB_INDEX.VISITS) {
            match = selectedFile.name.match(IS_VALID_SRT_VISIT_TYPE_REGEX);
        }

        if (!match) return;

        const nameType = match[1] as SRT_RESPONSE_RGRL_TXT_TYPE & SRT_RESPONSE_VISITS_TXT_TYPE;

        try {
            const formData = new FormData();
            formData.append('file', selectedFile);
            let response: any;
            if(currentTab === TAB_INDEX.RGRL) {
                response = await uploadRGRLResponseSRT({ nameType, body: formData });
            }

            if(currentTab === TAB_INDEX.VISITS) {
                response = await uploadVisitsResponseSRT({ nameType, body: formData });
            }

            if (response?.error) {
                handleErrors(response?.error);
                return;
            }
            const newErrors = response?.data?.data?.establecimientos;

            if (newErrors) {
                const mergedErrors: Partial<Record<string, string[]>> | null = {
                    ...presentationUploadErrors
                };

                Object.entries(newErrors).forEach(([cuit, newValues]) => {
                    const existingValues = mergedErrors[cuit] || [];
                    const uniqueValues = Array.from(
                        new Set([...existingValues, ...(newValues as any)])
                    );
                    mergedErrors[cuit] = uniqueValues;
                });

                dispatch(setPresentationUploadErrorLocationData(mergedErrors));
            }
            enqueueSnackbar(translate('drawers.uploadResponseSRT.enqueues.uploadSRTFileSuccess'), {
                variant: 'success'
            });
            handleCloseDrawer();
        } catch (error) {
            enqueueSnackbar(translate('drawers.uploadResponseSRT.enqueues.uploadSRTSFileFailure'), {
                variant: 'error'
            });
        }
    };

    return (
        <Box
            display="flex"
            justifyContent="space-between"
            sx={{ flexDirection: 'column', height: '100%' }}
        >
            <Grid container>
                <DrawerHeader
                    onIconClick={handleCloseDrawer}
                    text={translate('drawers.uploadResponseSRT.title')}
                />
                <Grid item xs={12} textAlign={'left'} pl={2} pr={2}>
                    <Box
                        sx={{
                            borderRadius: '4px',
                            p: 2,
                            mt: 8,
                            cursor: 'pointer',
                            backgroundColor: '#FFFFFF',
                            maxWidth: '100%',
                            textAlign: 'left',
                            border: '1px solid #E0EBED',
                            display: 'flex',
                            alignItems: 'center'
                        }}
                        component="label"
                    >
                        <UploadFileOutlined sx={{ mr: 1 }} />
                        <Typography sx={{ color: 'gray' }}>
                            {translate('drawers.uploadResponseSRT.attachFile')}
                        </Typography>
                        <input
                            type="file"
                            hidden
                            accept=".txt"
                            onChange={handleFileChange}
                            ref={fileInputRef}
                            data-testid={TEST_IDS.drawers.uploadResponse.inputs.file}
                        />
                    </Box>
                    {fileName && (
                        <Typography
                            variant="body2"
                            mt={1}
                            sx={{ color: '#476773', fontSize: '12px' }}
                            data-testid={TEST_IDS.drawers.uploadResponse.text.fileName}
                        >
                            {fileName}
                        </Typography>
                    )}
                    <Typography
                        variant="body2"
                        mt={0.4}
                        sx={{ color: '#476773', fontSize: '12px' }}
                        data-testid={TEST_IDS.drawers.uploadResponse.text.allowedFiles}
                    >
                        {translate('drawers.uploadResponseSRT.allowedFiles')}
                    </Typography>
                </Grid>
            </Grid>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                    data-testid={TEST_IDS.drawers.uploadResponse.buttons.cancel}
                    variant="outlined"
                    onClick={handleCloseDrawer}
                    sx={{ mr: 1 }}
                >
                    {translate('drawers.buttons.cancel')}
                </Button>
                <Button
                    data-testid={TEST_IDS.drawers.uploadResponse.buttons.submit}
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={!selectedFile || isUploadingRGRLResponse || isUploadingVisitsResponse}
                >
                    {translate('drawers.buttons.saveResponseSRT')}
                </Button>
            </Box>
            <BackdropComponent loading={isUploadingRGRLResponse} />
        </Box>
    );
};

export default UploadResponseSRTDrawer;
